import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { PlayCircle, Eye, ArrowRight, Sparkles, Calendar, ChevronLeft } from 'lucide-react';
import toast from 'react-hot-toast';

const backendUrl = 'https://justurbane.in/backend_php/public';

const DigitalMagazineDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [magazine, setMagazine] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper function to format bytes into KB/MB/GB
const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return "0 B";
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i];
  };
  

  // Fetch magazine by ID
  useEffect(() => {
    const fetchMagazine = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/admin/digital_magazines/${id}`);
        if (!res.ok) throw new Error('Failed to fetch magazine');
        const data = await res.json();
        setMagazine(data);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load magazine');
      } finally {
        setLoading(false);
      }
    };

    fetchMagazine();
  }, [id]);

  if (loading) return <p className="p-4">Loading...</p>;
  if (!magazine) return <p className="p-4">Magazine not found</p>;

//   const openMagazineReader = () => {
//     // navigate('/magazine-reader');
//     navigate('/magazine-reader', { state: { pdfUrl: magazine.pdf_url , displayPages: magazine.display_pages } });      
//   };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/digital_magazines')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Back to Magazines</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-bold text-gray-900">Magazine Management</h1>
            </div>
            
          </div>
        </div>
      </header>

      {/* Main Content Section */}
        <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Magazine Details</h2>
            <div className="flex gap-3">
            {magazine.pdf_url && (
                <a
                href={magazine.pdf_url}
                target = "_blank"
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
              >
                View PDF
              </a>
              
            )}
            
            </div>
        </div>

        {/* Main Layout: Cover + Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Cover Image */}
            <div className="col-span-1">
            <div className="border rounded-lg overflow-hidden shadow-sm bg-gray-50">
                <img
                src={magazine.cover_image}
                alt={magazine.title}
                className="w-full object-cover aspect-[3/4]"
                />
            </div>
            </div>

            {/* Right: Magazine Info */}
            <div className="col-span-2">
            <div className="bg-white border rounded-lg shadow-sm p-6 space-y-6">
                {/* Title & Description */}
                <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{magazine.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed break-words">
                {magazine.description}
                </p>
                </div>

                {/* Stats in Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {magazine.pages && (
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold text-gray-900">{magazine.pages}</div>
                    <div className="text-sm text-gray-600">Pages</div>
                    </div>
                )}
                {magazine.articles_count && (
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold text-gray-900">{magazine.articles_count}</div>
                    <div className="text-sm text-gray-600">Articles</div>
                    </div>
                )}
                {magazine.read_time && (
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold text-gray-900">{magazine.read_time} min</div>
                    <div className="text-sm text-gray-600">Read Time</div>
                    </div>
                )}
                </div>

                {/* Details Table */}
                <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full border-collapse">
                    <tbody>
                    <tr className="border-b">
                        <td className="px-4 py-3 text-sm font-medium text-gray-600 w-1/3">Month & Year</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                        {magazine.month} {magazine.year}
                        </td>
                    </tr>
                    <tr className="border-b">
                        <td className="px-4 py-3 text-sm font-medium text-gray-600">Published At</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{magazine.published_at}</td>
                    </tr>
                    {magazine.file_size && (
                        <tr className="border-b">
                        <td className="px-4 py-3 text-sm font-medium text-gray-600">File Size</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{formatFileSize(magazine.file_size)}</td>
                        </tr>
                    )}
                    <tr>
                        <td className="px-4 py-3 text-sm font-medium text-gray-600">Featured</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                        {magazine.is_featured ? (
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            Yes
                            </span>
                        ) : (
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                            No
                            </span>
                        )}
                        </td>
                    </tr>
                    </tbody>
                </table>
                </div>
            </div>
            </div>
        </div>
        </div>


    </div>
  );
};

export default DigitalMagazineDetailPage;
